import {
  addDays,
  endOfWeek,
  format,
  isSameDay,
  isValid,
  parse,
  parseISO,
  startOfWeek,
} from "date-fns";
import { LESSON_STATUS, PHASES, SECTION_TYPES, SUBJECT_TONES } from "../LessonPlannerConstant";

/* ================= DATES ================= */

export const parseDate = (value) => {
  if (!value) return null;
  const date = value instanceof Date ? value : parseISO(value);
  return isValid(date) ? date : null;
};

export const toApiDate = (value) => {
  const date = parseDate(value);
  return date ? format(date, "yyyy-MM-dd") : "";
};

export const formatDate = (value, pattern = "dd MMM yyyy") => {
  const date = parseDate(value);
  return date ? format(date, pattern) : "";
};

export const getWeekRange = (value = new Date()) => {
  const date = parseDate(value) || new Date();
  return {
    start: startOfWeek(date, { weekStartsOn: 1 }),
    end: endOfWeek(date, { weekStartsOn: 1 }),
  };
};

// "02 Feb - 08 Feb'26"
export const formatWeekLabel = (start, end) =>
  `${formatDate(start, "dd MMM")} - ${formatDate(end, "dd MMM''yy")}`;

export const getWeekDays = (weekStart) =>
  Array.from({ length: 7 }, (_, index) => addDays(weekStart, index));

// "09:30:00" | "09:30" -> "9:30 am"
export const formatTime = (value) => {
  if (!value) return "";
  const pattern = value.length > 5 ? "HH:mm:ss" : "HH:mm";
  const date = parse(value, pattern, new Date());
  return isValid(date) ? format(date, "h:mm aaa") : value;
};

export const formatTimeRange = (start, end) =>
  [formatTime(start), formatTime(end)].filter(Boolean).join(" – ");

/* ================= DISPLAY ================= */

export const getSubjectTone = (name = "") => {
  const hash = [...name].reduce((sum, char) => sum + char.charCodeAt(0), 0);
  return SUBJECT_TONES[hash % SUBJECT_TONES.length];
};

export const getPhaseMeta = (key) =>
  PHASES.find((phase) => phase.key === key) || { key, label: key, tone: "secondary" };

export const getTotalDuration = (phases = []) =>
  phases.reduce((sum, phase) => sum + (Number(phase.duration_minutes) || 0), 0);

export const getSuccessMessage = (response, fallback) => {
  const message = response?.data?.message;
  if (typeof message === "string") return message;
  return message?.success || fallback;
};

/* ================= API -> UI ================= */

const toText = (item, ...keys) => {
  if (typeof item === "string") return item;
  const key = keys.find((k) => item?.[k]);
  return key ? item[key] : "";
};

const normalizeSection = (section) => ({
  name: section.name || "",
  sub_heading: section.sub_heading || "",
  type: section.type === SECTION_TYPES.BULLET_LIST ? SECTION_TYPES.BULLET_LIST : SECTION_TYPES.RICH_TEXT,
  items: (section.items || []).map((item) => toText(item, "text", "item")),
  content: section.content || "",
});

/**
 * Maps one lesson (or an empty timetable slot) from the API into the shape the UI uses.
 * A slot without a lesson plan is sent with `has_lesson_plan: false`.
 */
export const normalizeLesson = (raw = {}) => ({
  id: raw.id ?? null,
  hasPlan: raw.has_lesson_plan ?? Boolean(raw.id),
  title: raw.title || "",
  teacherId: raw.teacher_id ?? raw.teacher?.id ?? "",
  teacherName: raw.teacher_name ?? raw.teacher?.name ?? "",
  subjectId: raw.subject_id ?? raw.subject?.id ?? "",
  subjectName: raw.subject_name ?? raw.subject?.name ?? "",
  // Diary endpoints send the class as `class: { id, name }`
  programId: raw.program_id ?? raw.program?.id ?? raw.class?.id ?? "",
  programName: raw.program_name ?? raw.program?.name ?? raw.class?.name ?? "",
  creationDate: raw.creation_date || "",
  lessonDate: raw.lesson_date || raw.date || "",
  startTime: raw.start_time || "",
  endTime: raw.end_time || "",
  period: raw.period ?? "",
  status: raw.status === LESSON_STATUS.COMPLETED ? LESSON_STATUS.COMPLETED : LESSON_STATUS.PENDING,
  // The list API marks flags with a `flagged_at` timestamp
  isFlagged: Boolean(raw.is_flagged ?? raw.flagged ?? raw.flagged_at),
  // Flag reason, else the remarks given on completion
  remark: raw.flag_reason ?? raw.reason ?? raw.remarks ?? raw.completion_remarks ?? raw.remark ?? "",
  // Flag details (may also come nested as `flag: { reason, remarks, revised_delivery_date }`)
  flagReason: raw.flag_reason ?? raw.flag?.reason ?? "",
  flagRemarks: raw.flag_remarks ?? raw.flag?.remarks ?? "",
  revisedDeliveryDate: raw.revised_delivery_date ?? raw.flag?.revised_delivery_date ?? "",
  reviewTag: raw.review_tag || "",
  reflection: raw.reflection || "",
  unitChapter: raw.unit_chapter || "",
  phases: raw.phases || [],
  objectives: (raw.learning_objectives || []).map((o) => toText(o, "objective", "text")),
  materials: (raw.materials || []).map((m) => toText(m, "name", "material")),
  assessment: raw.assessment || "",
  homework: raw.homework || "",
  sections: (raw.sections || []).map(normalizeSection),
});

const countSummary = (lessons) => {
  const planned = lessons.filter((lesson) => lesson.hasPlan);
  return {
    total: planned.length,
    completed: planned.filter((l) => l.status === LESSON_STATUS.COMPLETED).length,
    pending: planned.filter((l) => l.status === LESSON_STATUS.PENDING).length,
    flagged: planned.filter((l) => l.isFlagged).length,
  };
};

/**
 * Accepts either `data: [...]` or `data: { lessons: [...], summary: {...} }`.
 * Summary values from the API win; anything missing is counted client-side.
 */
export const normalizeLessonList = (response) => {
  const payload = response?.data?.data ?? [];
  const list = Array.isArray(payload) ? payload : payload.lessons ?? payload.data ?? [];
  const lessons = list.map(normalizeLesson);
  const counted = countSummary(lessons);
  const summary = Array.isArray(payload) ? {} : payload.summary || {};

  return {
    lessons,
    summary: {
      total: summary.total ?? counted.total,
      completed: summary.completed ?? summary.taught ?? counted.completed,
      pending: summary.pending ?? counted.pending,
      flagged: summary.flagged ?? counted.flagged,
    },
    reflection: Array.isArray(payload) ? "" : payload.reflection || "",
    // Laravel paginator fields (GET /lesson-plan is paginated)
    lastPage: Array.isArray(payload) ? 1 : payload.last_page || 1,
    totalRecords: Array.isArray(payload) ? lessons.length : payload.total ?? lessons.length,
    isSubmitted: Array.isArray(payload) ? false : Boolean(payload.is_submitted),
  };
};

const byStartTime = (a, b) => (a.startTime || "").localeCompare(b.startTime || "");

/** Groups lessons by day of the week, keeping only days that have lessons. */
export const groupLessonsByDay = (lessons, weekStart) =>
  getWeekDays(weekStart)
    .map((date) => ({
      date,
      lessons: lessons
        .filter((lesson) => isSameDay(parseDate(lesson.lessonDate), date))
        .sort(byStartTime),
    }))
    .filter((day) => day.lessons.length > 0);

/* ================= FORM <-> API ================= */

let uidCounter = 0;
export const createUid = () => `uid-${Date.now()}-${uidCounter++}`;

export const createSection = ({ name, sub_heading, type }) => ({
  uid: createUid(),
  name,
  sub_heading,
  type,
  items: type === SECTION_TYPES.BULLET_LIST ? [""] : [],
  content: "",
});

export const getInitialForm = (defaults = {}) => ({
  title: "",
  subject_id: "",
  program_id: "",
  creation_date: toApiDate(new Date()),
  lesson_date: "",
  unit_chapter: "",
  phases: PHASES.map((phase) => ({
    phase: phase.key,
    duration_minutes: phase.defaultDuration,
    content: "",
  })),
  learning_objectives: [""],
  materials: [],
  assessment: "",
  homework: "",
  sections: [],
  ...defaults,
});

/** Builds the form state from a normalized lesson (edit mode). */
export const lessonToForm = (lesson) =>
  getInitialForm({
    title: lesson.title,
    subject_id: String(lesson.subjectId ?? ""),
    program_id: String(lesson.programId ?? ""),
    creation_date: toApiDate(lesson.creationDate) || toApiDate(new Date()),
    lesson_date: toApiDate(lesson.lessonDate),
    unit_chapter: lesson.unitChapter,
    phases: PHASES.map((meta) => {
      const saved = lesson.phases.find((p) => p.phase === meta.key) || {};
      return {
        phase: meta.key,
        duration_minutes: saved.duration_minutes ?? meta.defaultDuration,
        content: saved.content || "",
      };
    }),
    learning_objectives: lesson.objectives.length ? lesson.objectives : [""],
    materials: lesson.materials,
    assessment: lesson.assessment,
    homework: lesson.homework,
    sections: lesson.sections.map((section) => ({ ...section, uid: createUid() })),
  });

const compact = (list) => list.map((item) => item.trim()).filter(Boolean);

const BLOCK_TAG = /<(p|ul|ol|h[1-6]|blockquote)[\s>]/i;

/**
 * Normalises editor HTML to paragraph markup, e.g.
 * "Text <b>bold</b><div>Next</div>" -> "<p>Text <b>bold</b></p><p>Next</p>".
 * Browsers' contentEditable leaves the first line bare and wraps later lines in <div>.
 */
export const toParagraphHtml = (html = "") => {
  const normalized = html
    .replace(/<div([^>]*)>/gi, "<p$1>")
    .replace(/<\/div>/gi, "</p>")
    .replace(/&nbsp;/gi, " ")
    // drop empty paragraphs such as "<p><br></p>"
    .replace(/<p[^>]*>(\s|<br\s*\/?>)*<\/p>/gi, "")
    .trim();
  if (!normalized) return "";

  // Wrap leading inline content (before the first block element) in <p>
  const blockMatch = normalized.match(BLOCK_TAG);
  const blockStart = blockMatch ? blockMatch.index : normalized.length;
  const leading = normalized.slice(0, blockStart).replace(/(<br\s*\/?>)+$/i, "").trim();
  const rest = normalized.slice(blockStart);
  return `${leading ? `<p>${leading}</p>` : ""}${rest}`;
};

/** Builds the POST/PUT body in the format agreed with the backend. */
export const buildLessonPlanPayload = (form) => ({
  title: form.title.trim(),
  subject_id: Number(form.subject_id),
  program_id: Number(form.program_id),
  creation_date: form.creation_date,
  lesson_date: form.lesson_date || null,
  unit_chapter: form.unit_chapter.trim(),
  phases: form.phases.map(({ phase, duration_minutes, content }) => ({
    phase,
    duration_minutes: Number(duration_minutes) || 0,
    content: content.trim(),
  })),
  learning_objectives: compact(form.learning_objectives),
  materials: compact(form.materials),
  assessment: form.assessment.trim(),
  homework: form.homework.trim(),
  sections: form.sections.map(({ name, sub_heading, type, items, content }) =>
    type === SECTION_TYPES.BULLET_LIST
      ? { name, sub_heading, type, items: compact(items) }
      : { name, sub_heading, type, content: toParagraphHtml(content) },
  ),
});

export const validateLessonPlan = (form) => {
  const errors = {};
  if (!form.title.trim()) errors.title = "Lesson title is required";
  if (!form.program_id) errors.program_id = "Class & section is required";
  if (!form.subject_id) errors.subject_id = "Subject is required";
  if (!form.creation_date) errors.creation_date = "Creation date is required";
  if (form.lesson_date && form.lesson_date < form.creation_date) {
    errors.lesson_date = "Lesson date cannot be before the creation date";
  }
  return errors;
};

/* ================= SESSION ================= */

export const getCurrentUserId = () => {
  try {
    return JSON.parse(localStorage.getItem("user"))?.id ?? "";
  } catch {
    return "";
  }
};

export const hasPermission = (permission) => {
  try {
    return (JSON.parse(localStorage.getItem("permissions")) || []).includes(permission);
  } catch {
    return false;
  }
};

/* ================= OPTIONS ================= */

/** Any `[{ id, name | title | label }]` list -> `[{ id, name }]` for the shared Select components. */
export const toOptions = (list) =>
  (Array.isArray(list) ? list : [])
    .map((item) => ({
      ...item,
      id: item.id ?? item.value,
      name: item.name ?? item.title ?? item.label ?? "",
    }))
    .filter((item) => item.id !== undefined && item.id !== null);
